// src/pages/SheetEdit.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Skeleton,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";

import { db } from "../firebase-config";
import Form from "../components/Form";

const snapToArr = (snap) => (snap?.docs || []).map((d) => ({ id: d.id, ...d.data() }));

export default function SheetEdit() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // sheet meta (always shown above the form)
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public"); // "private" | "public"

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setError("");
      setLoading(true);
      try {
        const sheetRef = doc(db, "sheets", sheetId);
        const sheetSnap = await getDoc(sheetRef);
        if (!sheetSnap.exists()) throw new Error("Sheet not found.");

        const data = sheetSnap.data() || {};

        const [vSnap, secSnap, itemSnap] = await Promise.all([
          getDocs(query(collection(db, "sheets", sheetId, "variables"), orderBy("name"), limit(500))).catch(
            () => null
          ),
          getDocs(query(collection(db, "sheets", sheetId, "sections"), orderBy("order"), limit(500))).catch(
            () => null
          ),
          getDocs(query(collection(db, "sheets", sheetId, "items"), orderBy("order"), limit(1000))).catch(
            () => null
          ),
        ]);

        if (!alive) return;

        // header fields
        setName(data.name || "");
        setDescription(data.description || "");
        setVisibility(data.visibility || "private");

        setSheet({
          // keep these for the Form as well (if it uses them)
          name: data.name || "",
          description: data.description || "",
          version: Number.isFinite(data.version) ? data.version : parseInt(data.version || "1", 10) || 1,

          // loaded from subcollections
          variables: snapToArr(vSnap),
          sections: snapToArr(secSnap),
          items: snapToArr(itemSnap),
        });
      } catch (e) {
        if (alive) setError(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [sheetId]);

  const handleSave = async (payload) => {
    setError("");
    setSaving(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in.");

      if (!name.trim()) throw new Error("Name is required.");

      const sheetRef = doc(db, "sheets", sheetId);
      const nextVersion = (Number(sheet?.version) || 1) + 1;

      // split sheet fields vs subcollections
      const { variables = [], sections = [], items = [], ...sheetFields } = payload || {};

      // update main sheet doc (NO arrays stored here)
      await updateDoc(sheetRef, {
        ...sheetFields,

        // meta fields from header
        name: name.trim(),
        description: description.trim(),
        visibility,

        version: nextVersion,
        updatedAt: serverTimestamp(),
      });

      // upsert + delete removed docs in subcollections
      const batch = writeBatch(db);
      const now = serverTimestamp();

      const writeCollection = (sub, rows, prevRows) => {
        const colRef = collection(db, "sheets", sheetId, sub);

        const incomingIds = new Set(
          (rows || [])
            .map((r) => r?.id)
            .filter((id) => typeof id === "string" && id.length)
        );

        const prevIds = new Set(
          (prevRows || [])
            .map((r) => r?.id)
            .filter((id) => typeof id === "string" && id.length)
        );

        // deletes (present before, missing now)
        for (const id of prevIds) {
          if (!incomingIds.has(id)) batch.delete(doc(colRef, id));
        }

        // upserts
        for (const row of rows || []) {
          if (!row) continue;

          const { id, ...data } = row;

          // ensure stable doc id
          const rowRef =
            typeof id === "string" && id.length ? doc(colRef, id) : doc(colRef);

          batch.set(
            rowRef,
            {
              ...data,
              updatedAt: now,
              createdAt: data?.createdAt || now,
            },
            { merge: true }
          );
        }
      };

      writeCollection("variables", variables, sheet?.variables);
      writeCollection("sections", sections, sheet?.sections);
      writeCollection("items", items, sheet?.items);

      await batch.commit();

      navigate(`/sheet/${sheetId}`);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={450} />
      </Stack>
    );
  }

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        <FormControl fullWidth>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={visibility}
            label="Visibility"
            onChange={(e) => setVisibility(e.target.value)}
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="public">Public</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Form initialData={sheet} onSave={handleSave} saving={saving} error={error} />
    </Box>
  );
}
